import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft, Send, Clock, CheckCircle2, AlertTriangle, Pause,
  XCircle, Ticket, ArrowUpCircle, ArrowRightCircle, ArrowDownCircle, User,
  Mail, RefreshCw, Link2, Paperclip, X, FileText, Download, Loader2,
  ArrowRightLeft, Users as UsersIcon, Edit2, Zap, Search as SearchIcon, UserRoundCog, Eye,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { canEditModule } from "@/utils/permissions";
import DocumentPreviewDialog from "@/components/DocumentPreviewDialog";

const STATUS_MAP = {
  abierto: { label: "Abierto", color: "bg-blue-500 text-white" },
  en_progreso: { label: "En Progreso", color: "bg-amber-500 text-white" },
  en_espera: { label: "En Espera", color: "bg-purple-500 text-white" },
  resuelto: { label: "Resuelto", color: "bg-green-500 text-white" },
  cerrado: { label: "Cerrado", color: "bg-slate-500 text-white" },
};

const PRIORITY_MAP = {
  baja: { label: "Baja", color: "bg-slate-100 text-slate-700" },
  media: { label: "Media", color: "bg-blue-100 text-blue-700" },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-700" },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-700" },
};

const DEFAULT_CATEGORIES = ["TI", "Mantenimiento", "Traveldesk", "Logística", "Administración"];

const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024; // 25MB (Gmail)
const MAX_ATTACHMENTS_PER_MESSAGE = 10;
const BLOCKED_EXTENSIONS = new Set([
  "ade","adp","apk","appx","appxbundle","bat","cab","chm","cmd","com","cpl",
  "dll","dmg","ex","ex_","exe","hta","ins","isp","iso","jar","js","jse","lib",
  "lnk","mde","msc","msi","msix","msixbundle","msp","mst","nsh","pif","ps1",
  "scr","sct","shb","sys","vb","vbe","vbs","vxd","wsc","wsf","wsh",
]);

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const HelpdeskTicketDetail = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [categorias, setCategorias] = useState(DEFAULT_CATEGORIES);
  const [comment, setComment] = useState("");
  const [attachments, setAttachments] = useState([]); // file_refs already uploaded
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [checkingEmails, setCheckingEmails] = useState(false);
  const [ccEditing, setCcEditing] = useState(false);
  const [ccDraft, setCcDraft] = useState("");
  const [includeSignature, setIncludeSignature] = useState(false);
  const [quickReplies, setQuickReplies] = useState({ defaults: [], personal: [] });
  const [qrPickerOpen, setQrPickerOpen] = useState(false);
  const [qrSearch, setQrSearch] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [transferNote, setTransferNote] = useState("");
  const [transferAssignee, setTransferAssignee] = useState("");
  // Perfil de Viajero vinculado al ticket (si aplica)
  const [perfilInfo, setPerfilInfo] = useState(null); // null | {has_perfil, perfil?, project_nombre?}
  const [perfilOpen, setPerfilOpen] = useState(false);

  const canEdit = canEditModule(user, "helpdesk_tickets");
  const canManageCc = user?.role === "admin" || user?.role === "supervisor";

  useEffect(() => {
    fetchTicket();
    axios.get(`${API}/users-list`, { withCredentials: true }).then(r => setEmployees(r.data)).catch(() => {});
    axios.get(`${API}/settings`, { withCredentials: true }).then(r => setDepartamentos(r.data?.departamentos_operativos || [])).catch(() => {});
    axios.get(`${API}/ticket-categorias`, { withCredentials: true }).then(r => setCategorias(r.data.map(c => c.nombre))).catch(() => {});
    axios.get(`${API}/oauth/gmail/status`, { withCredentials: true }).then(r => {
      setGmailStatus(r.data);
      // Auto-check emails if Gmail is connected
      if (r.data?.connected) {
        axios.post(`${API}/tickets/check-emails`, {}, { withCredentials: true })
          .then(res => { if (res.data?.processed > 0) fetchTicket(); })
          .catch(() => {});
      }
    }).catch(() => {});
    axios.get(`${API}/quick-replies`, { withCredentials: true })
      .then(r => setQuickReplies(r.data || { defaults: [], personal: [] }))
      .catch(() => {});
  }, [id]);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tickets/${id}`, { withCredentials: true });
      setTicket(res.data);
      // Chequea si el ticket tiene perfil de viajero vinculado (solo aplica a
      // tickets provenientes de un formulario con perfil ligado).
      if (res.data?.source === "formulario") {
        axios.get(`${API}/tickets/${id}/perfil-viajero`, { withCredentials: true })
          .then((r) => setPerfilInfo(r.data || null))
          .catch(() => setPerfilInfo({ has_perfil: false }));
      } else {
        setPerfilInfo({ has_perfil: false });
      }
    } catch (err) {
      toast.error("Ticket no encontrado");
      navigate("/helpdesk/tickets");
    } finally {
      setLoading(false);
    }
  };

  const updateField = async (field, value) => {
    try {
      const data = { [field]: value };
      if (field === "assigned_to_id") {
        const emp = employees.find(e => e.id === value);
        data.assigned_to_name = emp?.name || "";
      }
      const res = await axios.put(`${API}/tickets/${id}`, data, { withCredentials: true });
      setTicket(res.data);
      toast.success("Ticket actualizado");
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const addComment = async () => {
    if (!comment.trim() && attachments.length === 0) return;
    if (!ticket?.assigned_to_id) {
      toast.error("Asigna el ticket a un agente antes de responder.");
      return;
    }
    setSending(true);
    try {
      let textToSend = comment;
      if (includeSignature && comment.trim()) {
        const signer = user?.name || user?.email || "";
        if (signer) {
          textToSend = `${comment.trim()}\n\nSaludos,\n${signer}`;
        }
      }
      const payload = { text: textToSend, attachments };
      if (canManageCc && ccEditing) {
        // Parse CC emails from text area
        const parsed = ccDraft.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
        payload.cc_emails = parsed;
      }
      await axios.post(`${API}/tickets/${id}/comments`, payload, { withCredentials: true });
      setComment("");
      setAttachments([]);
      setCcEditing(false);
      fetchTicket();
      toast.success("Comentario enviado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al agregar comentario");
    } finally {
      setSending(false);
    }
  };

  // Insert a quick reply template into the comment textarea.
  // Template body_html is converted to plain text (preserves line breaks)
  // because the comment field is a plain textarea / email body.
  const insertQuickReply = (qr) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = qr.body_html || "";
    // Convert <br> and block-ish elements to newlines for readability
    tmp.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    tmp.querySelectorAll("p,div,li,h1,h2,h3,tr").forEach((el) => {
      el.appendChild(document.createTextNode("\n"));
    });
    const text = (tmp.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
    setComment((prev) => (prev ? `${prev}\n\n${text}` : text));
    setQrPickerOpen(false);
    setQrSearch("");
  };

  const handleTransfer = async () => {
    if (!transferTo) return toast.error("Selecciona el departamento destino");
    try {
      await axios.post(`${API}/tickets/${id}/transfer`, {
        to_departamento: transferTo,
        note: transferNote,
        new_assigned_to_id: transferAssignee || null,
        new_assigned_to_name: transferAssignee ? employees.find(e => e.id === transferAssignee)?.name : null,
      }, { withCredentials: true });
      toast.success("Ticket transferido");
      setTransferOpen(false);
      setTransferTo(""); setTransferNote(""); setTransferAssignee("");
      fetchTicket();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al transferir");
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file
    if (files.length === 0) return;

    if (attachments.length + files.length > MAX_ATTACHMENTS_PER_MESSAGE) {
      toast.error(`Máximo ${MAX_ATTACHMENTS_PER_MESSAGE} archivos por mensaje`);
      return;
    }

    const currentTotal = attachments.reduce((s, a) => s + (a.size || 0), 0);
    let addedSize = 0;

    // Validate first
    for (const f of files) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (BLOCKED_EXTENSIONS.has(ext)) {
        toast.error(`Tipo de archivo bloqueado: .${ext}`);
        return;
      }
      if (f.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`"${f.name}" supera 25MB`);
        return;
      }
      addedSize += f.size;
    }
    if (currentTotal + addedSize > MAX_ATTACHMENT_SIZE) {
      toast.error("El tamaño total supera 25MB");
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/tickets/${id}/attachments`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data);
      }
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (fileId) => {
    setAttachments((prev) => prev.filter((a) => a.id !== fileId));
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await axios.get(`${API}/tickets/attachments/${att.storage_path}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: att.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "archivo";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Error al descargar archivo");
    }
  };

  const connectGmail = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/authorize`, { withCredentials: true });
      const popup = window.open(res.data.auth_url, "gmail_auth", "width=500,height=700");
      const handleMessage = (e) => {
        if (e.data?.type === "gmail_connected") {
          setGmailStatus({ connected: true, email: e.data.email });
          toast.success(`Gmail conectado: ${e.data.email}`);
          window.removeEventListener("message", handleMessage);
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (err) {
      toast.error("Error al conectar Gmail");
    }
  };

  const checkEmails = async () => {
    setCheckingEmails(true);
    try {
      const res = await axios.post(`${API}/tickets/check-emails`, {}, { withCredentials: true });
      if (res.data.processed > 0) {
        toast.success(`${res.data.processed} respuesta(s) de email procesada(s)`);
        fetchTicket();
      } else {
        toast.info("No hay nuevos emails");
      }
    } catch (err) {
      toast.error("Error al revisar emails");
    } finally {
      setCheckingEmails(false);
    }
  };

  const fmtDateTime = (d) => d ? new Date(d).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  if (loading) return <div className="p-6 text-center text-slate-500">Cargando...</div>;
  if (!ticket) return null;

  const st = STATUS_MAP[ticket.status] || STATUS_MAP.abierto;
  const pr = PRIORITY_MAP[ticket.priority] || PRIORITY_MAP.media;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="ticket-detail-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/helpdesk/tickets")} data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-blue-600 font-semibold">{ticket.folio}</span>
              <Badge className={st.color}>{st.label}</Badge>
              <Badge className={pr.color}>{pr.label}</Badge>
              {ticket.source === "formulario_externo" && <Badge variant="outline">Link Soporte</Badge>}
              {perfilInfo?.has_perfil && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 h-7"
                  onClick={() => setPerfilOpen(true)}
                  data-testid="ticket-btn-perfil-viajero"
                >
                  <UserRoundCog className="w-4 h-4 mr-1" /> Perfil de Viajero
                </Button>
              )}
            </div>
            <h1 className="text-xl font-bold text-slate-800 mt-1">{ticket.title}</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Description + Comments */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader><CardTitle className="text-base">Descripción</CardTitle></CardHeader>
              <CardContent>
                {ticket.description ? (
                  /<\w+[\s\S]*>/.test(ticket.description) ? (
                    <div
                      className="text-sm text-slate-700 prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0.5"
                      dangerouslySetInnerHTML={{ __html: ticket.description }}
                    />
                  ) : (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</p>
                  )
                ) : (
                  <p className="text-sm text-slate-400">Sin descripción</p>
                )}
              </CardContent>
            </Card>

            {/* Comments */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Conversación ({ticket.comments?.length || 0})</CardTitle>
                {gmailStatus.connected && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={checkEmails}
                    disabled={checkingEmails}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    data-testid="refresh-emails-btn"
                    title="Buscar nuevas respuestas en Gmail"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${checkingEmails ? "animate-spin" : ""}`} />
                    {checkingEmails ? "Revisando..." : "Revisar correos"}
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {(!ticket.comments || ticket.comments.length === 0) && (
                  <p className="text-sm text-slate-400 text-center py-4">Sin comentarios aún</p>
                )}
                {ticket.comments?.map(c => (
                  <div key={c.id} className="flex gap-3">
                    <div className="flex-shrink-0">
                      {c.author_picture ? (
                        <img src={c.author_picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${c.source === "email" ? "bg-blue-100" : "bg-slate-200"}`}>
                          {c.source === "email" ? <Mail className="w-4 h-4 text-blue-500" /> : <User className="w-4 h-4 text-slate-500" />}
                        </div>
                      )}
                    </div>
                    <div className={`flex-1 rounded-lg p-3 ${c.source === "email" ? "bg-blue-50 border border-blue-100" : "bg-slate-50"}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">{c.author_name}</span>
                        {c.source === "email" && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><Mail className="w-2.5 h-2.5 mr-0.5" />Email</Badge>}
                        <span className="text-xs text-slate-400">{fmtDateTime(c.created_at)}</span>
                      </div>
                      {c.text && (
                        /<\w+[\s\S]*>/.test(c.text) ? (
                          <div
                            className="text-sm text-slate-700 prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0.5"
                            dangerouslySetInnerHTML={{ __html: c.text }}
                          />
                        ) : (
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.text}</p>
                        )
                      )}
                      {c.attachments && c.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2" data-testid={`comment-attachments-${c.id}`}>
                          {c.attachments.map((att) => (
                            <button
                              key={att.id}
                              onClick={() => downloadAttachment(att)}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-xs text-slate-700 group"
                              data-testid={`attachment-${att.id}`}
                              title={`Descargar ${att.filename}`}
                            >
                              <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                              <span className="truncate max-w-[180px]">{att.filename}</span>
                              <span className="text-slate-400">({formatBytes(att.size)})</span>
                              <Download className="w-3 h-3 text-slate-400 group-hover:text-blue-600" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add comment */}
                <div className="pt-3 border-t space-y-2">
                  {canManageCc && (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <UsersIcon className="w-3.5 h-3.5" />
                      <span className="font-medium">CC:</span>
                      {ccEditing ? (
                        <>
                          <Input
                            value={ccDraft}
                            onChange={(e) => setCcDraft(e.target.value)}
                            placeholder="email1@ejemplo.com, email2@ejemplo.com"
                            className="flex-1 h-8 text-xs"
                            data-testid="cc-input"
                          />
                          <button
                            type="button"
                            onClick={() => setCcEditing(false)}
                            className="text-slate-400 hover:text-slate-600"
                            data-testid="cc-cancel"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-slate-500 truncate">
                            {(() => {
                              const lastCc = [...(ticket.comments || [])].reverse().find((c) => c.source === "email" && (c.cc_emails || []).length > 0)?.cc_emails || [];
                              return lastCc.length > 0 ? lastCc.join(", ") : "Sin copias en el hilo";
                            })()}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const lastCc = [...(ticket.comments || [])].reverse().find((c) => c.source === "email" && (c.cc_emails || []).length > 0)?.cc_emails || [];
                              setCcDraft(lastCc.join(", "));
                              setCcEditing(true);
                            }}
                            className="text-blue-600 hover:underline flex items-center gap-1"
                            data-testid="cc-edit-btn"
                          >
                            <Edit2 className="w-3 h-3" /> Editar
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="pending-attachments">
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-200 text-xs text-slate-700">
                          <FileText className="w-3.5 h-3.5 text-blue-600" />
                          <span className="truncate max-w-[180px]">{att.filename}</span>
                          <span className="text-slate-400">({formatBytes(att.size)})</span>
                          <button
                            onClick={() => removeAttachment(att.id)}
                            className="text-slate-400 hover:text-red-500"
                            data-testid={`remove-attachment-${att.id}`}
                            aria-label="Quitar"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {!ticket?.assigned_to_id && (
                    <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md" data-testid="assignee-required-warning">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                      <span>
                        Asigna el ticket a un agente en <strong>"Asignado a"</strong> para poder enviar respuestas.
                      </span>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <Textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={ticket?.assigned_to_id ? "Escribe un comentario..." : "Asigna el ticket para poder responder..."}
                        rows={2}
                        data-testid="comment-input"
                        disabled={!ticket?.assigned_to_id}
                        onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) addComment(); }}
                      />
                      {/* Quick replies picker */}
                      <div className="absolute bottom-2 right-2">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-amber-600 hover:bg-amber-50"
                          onClick={() => setQrPickerOpen(true)}
                          disabled={!ticket?.assigned_to_id}
                          title="Insertar respuesta rápida"
                          data-testid="qr-picker-btn"
                        >
                          <Zap className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 self-end">
                      <input
                        type="file"
                        id="ticket-file-input"
                        multiple
                        className="hidden"
                        onChange={handleFileSelect}
                        data-testid="attachment-file-input"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => document.getElementById("ticket-file-input")?.click()}
                        disabled={uploading || sending || !ticket?.assigned_to_id}
                        title="Adjuntar archivo"
                        data-testid="attach-btn"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                      </Button>
                      <Button
                        onClick={addComment}
                        size="icon"
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={sending || uploading || (!comment.trim() && attachments.length === 0) || !ticket?.assigned_to_id}
                        title={!ticket?.assigned_to_id ? "Asigna el ticket primero" : "Enviar comentario (Ctrl+Enter)"}
                        data-testid="send-comment-btn"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="include-signature-label">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                        checked={includeSignature}
                        onChange={(e) => setIncludeSignature(e.target.checked)}
                        data-testid="include-signature-checkbox"
                      />
                      Incluir firma
                      {includeSignature && (user?.name || user?.email) && (
                        <span className="text-slate-400 ml-1">
                          (Saludos, {user?.name || user?.email})
                        </span>
                      )}
                    </label>
                    <p className="text-[11px] text-slate-400">
                      Máx. 25 MB por mensaje · Se envían como adjuntos por Gmail si el ticket es externo.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Details Panel */}
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Detalles</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Status */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Estado</Label>
                  {canEdit ? (
                    <Select value={ticket.status} onValueChange={(v) => updateField("status", v)}>
                      <SelectTrigger data-testid="detail-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={st.color}>{st.label}</Badge>
                  )}
                </div>

                {/* Priority */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Prioridad</Label>
                  {canEdit ? (
                    <Select value={ticket.priority} onValueChange={(v) => updateField("priority", v)}>
                      <SelectTrigger data-testid="detail-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITY_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={pr.color}>{pr.label}</Badge>
                  )}
                </div>

                {/* Category */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Categoría</Label>
                  {canEdit ? (
                    <Select value={ticket.category} onValueChange={(v) => updateField("category", v)}>
                      <SelectTrigger data-testid="detail-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categorias.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline">{ticket.category}</Badge>
                  )}
                </div>

                {/* Assigned */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Asignado a</Label>
                  {canEdit ? (
                    <Select value={ticket.assigned_to_id || "none"} onValueChange={(v) => updateField("assigned_to_id", v === "none" ? null : v)}>
                      <SelectTrigger data-testid="detail-assigned"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {employees.filter(e => e.is_active !== false).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm">{ticket.assigned_to_name || "Sin asignar"}</p>
                  )}
                </div>

                {/* Departamento operativo */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Departamento</Label>
                  {canEdit ? (
                    <div className="flex gap-2">
                      <Select value={ticket.departamento_name || "none"} onValueChange={(v) => updateField("departamento_name", v === "none" ? null : v)}>
                        <SelectTrigger data-testid="detail-departamento"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin asignar</SelectItem>
                          {departamentos.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(user?.role === "admin" || user?.role === "supervisor") && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setTransferOpen(true)}
                          title="Transferir ticket"
                          data-testid="transfer-btn"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline">{ticket.departamento_name || "Sin asignar"}</Badge>
                  )}
                </div>

                {/* Cliente */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Cliente</Label>
                  {canEdit ? (
                    <Input
                      value={ticket.cliente || ""}
                      onChange={(e) => updateField("cliente", e.target.value)}
                      placeholder="Empresa relacionada"
                      data-testid="detail-cliente"
                    />
                  ) : (
                    <p className="text-sm">{ticket.cliente || "—"}</p>
                  )}
                </div>

                {/* Due date */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Vencimiento de respuesta</Label>
                  {canEdit ? (
                    <Input
                      type="date"
                      value={ticket.due_date ? ticket.due_date.slice(0, 10) : ""}
                      onChange={(e) => updateField("due_date", e.target.value ? `${e.target.value}T23:59:59+00:00` : null)}
                      data-testid="detail-due-date"
                    />
                  ) : (
                    <p className="text-sm">{ticket.due_date ? fmtDateTime(ticket.due_date) : "—"}</p>
                  )}
                  {ticket.due_date && (
                    <p className="text-[11px] text-amber-600">
                      Se cerrará automáticamente un día después del vencimiento si sigue abierto.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Transfer history */}
            {(ticket.transfer_history && ticket.transfer_history.length > 0) && (
              <Card>
                <CardHeader><CardTitle className="text-base">Historial de transferencias</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-xs">
                  {ticket.transfer_history.map((t) => (
                    <div key={t.id} className="p-2 rounded border border-slate-200 bg-slate-50">
                      <div className="flex items-center gap-1.5 font-medium text-slate-700">
                        <Badge variant="outline" className="text-[10px]">{t.from || "—"}</Badge>
                        <ArrowRightLeft className="w-3 h-3 text-slate-400" />
                        <Badge className="bg-blue-100 text-blue-700 text-[10px]">{t.to}</Badge>
                      </div>
                      {t.note && <p className="mt-1 text-slate-600 whitespace-pre-wrap">{t.note}</p>}
                      <p className="mt-1 text-slate-400">por {t.by_name} · {fmtDateTime(t.at)}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Reopen history */}
            {(ticket.reopen_history && ticket.reopen_history.length > 0) && (
              <Card className="border-orange-200">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                    <RefreshCw className="w-4 h-4" />
                    Re-aperturas ({ticket.reopen_history.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-xs">
                  {ticket.reopen_history.map((r) => (
                    <div key={r.id} className="p-2 rounded border border-orange-200 bg-orange-50">
                      <p className="text-slate-700">
                        <strong>{r.reopened_by_name}</strong> respondió el {fmtDateTime(r.reopened_at)}
                      </p>
                      <p className="text-slate-500 mt-0.5">
                        Reabierto desde estado{" "}
                        <Badge variant="outline" className="text-[10px]">
                          {(r.previous_status || "resuelto").toUpperCase()}
                        </Badge>
                        {r.previous_resolved_at && (
                          <> · resuelto el {fmtDateTime(r.previous_resolved_at)}</>
                        )}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Info */}
            <Card>
              <CardContent className="p-4 space-y-3 text-sm">
                <div><span className="text-slate-500">Creado por:</span> <span className="font-medium">{ticket.created_by_name}</span></div>
                {ticket.created_by_email && <div><span className="text-slate-500">Email:</span> <span>{ticket.created_by_email}</span></div>}
                <div><span className="text-slate-500">Creado:</span> <span>{fmtDateTime(ticket.created_at)}</span></div>
                <div><span className="text-slate-500">Actualizado:</span> <span>{fmtDateTime(ticket.updated_at)}</span></div>
                {ticket.resolved_at && <div><span className="text-slate-500">Resuelto:</span> <span>{fmtDateTime(ticket.resolved_at)}</span></div>}
                <div><span className="text-slate-500">Fuente:</span> <span>{
                  (ticket.folio || "").toUpperCase().startsWith("COT-") || ticket.source === "formulario"
                    ? "Formulario Cotizaciones"
                    : ticket.source === "formulario_externo"
                      ? "Link Soporte"
                      : "Soporte Interno"
                }</span></div>
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-slate-500">Gmail:</span>
                    {gmailStatus.connected ? (
                      <span className="text-green-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Conectado</span>
                    ) : (
                      <span className="text-slate-400">No conectado</span>
                    )}
                  </div>
                  {!gmailStatus.connected && (
                    <p className="text-[11px] text-slate-400 mt-1">Conecta Gmail desde tu perfil en el menú lateral</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Transfer Dialog */}
        <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
          <DialogContent className="max-w-md" data-testid="transfer-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4" /> Transferir ticket
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">De</Label>
                <p className="text-sm font-medium">{ticket.departamento_name || "—"}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">A *</Label>
                <Select value={transferTo} onValueChange={setTransferTo}>
                  <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Selecciona departamento" /></SelectTrigger>
                  <SelectContent>
                    {departamentos.filter((d) => d !== ticket.departamento_name).map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Reasignar a (opcional)</Label>
                <Select value={transferAssignee || "none"} onValueChange={(v) => setTransferAssignee(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="transfer-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Mantener asignación actual</SelectItem>
                    {employees.filter((e) => e.is_active !== false).map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Motivo / notas</Label>
                <Textarea
                  value={transferNote}
                  onChange={(e) => setTransferNote(e.target.value)}
                  placeholder="Describe por qué transfieres este ticket..."
                  rows={3}
                  data-testid="transfer-note"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancelar</Button>
              <Button onClick={handleTransfer} className="bg-blue-700 hover:bg-blue-800" data-testid="transfer-submit">
                Transferir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quick replies picker */}
        <Dialog open={qrPickerOpen} onOpenChange={(o) => { if (!o) { setQrPickerOpen(false); setQrSearch(""); } }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" /> Insertar respuesta rápida
              </DialogTitle>
            </DialogHeader>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                autoFocus
                placeholder="Buscar plantilla..."
                value={qrSearch}
                onChange={(e) => setQrSearch(e.target.value)}
                className="pl-9"
                data-testid="qr-picker-search"
              />
            </div>
            <div className="overflow-y-auto space-y-4 mt-2 pr-1">
              {[
                { key: "defaults", label: "Por defecto", items: quickReplies.defaults },
                { key: "personal", label: "Personales", items: quickReplies.personal },
              ].map((section) => {
                const q = qrSearch.trim().toLowerCase();
                const filtered = q
                  ? section.items.filter(r =>
                      (r.title || "").toLowerCase().includes(q) ||
                      (r.shortcut || "").toLowerCase().includes(q))
                  : section.items;
                if (filtered.length === 0 && q) return null;
                return (
                  <div key={section.key}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                      {section.label} <span className="text-slate-300 font-normal">({filtered.length})</span>
                    </p>
                    {filtered.length === 0 ? (
                      <p className="text-xs text-slate-400 italic px-2 pb-1">Sin plantillas en esta sección.</p>
                    ) : (
                      <div className="space-y-1">
                        {filtered.map(r => (
                          <button
                            key={r.id}
                            onClick={() => insertQuickReply(r)}
                            className="w-full text-left p-2.5 rounded-md hover:bg-slate-100 border border-slate-200 transition-colors group"
                            data-testid={`qr-item-${r.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-800 flex-1 truncate">{r.title}</span>
                              {r.shortcut && (
                                <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 group-hover:bg-white">{r.shortcut}</code>
                              )}
                            </div>
                            <div
                              className="text-xs text-slate-500 mt-1 line-clamp-2 prose-sm"
                              dangerouslySetInnerHTML={{ __html: r.body_html || "<em>(sin contenido)</em>" }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {(quickReplies.defaults.length === 0 && quickReplies.personal.length === 0) && (
                <div className="text-center py-8 text-sm text-slate-400">
                  Aún no hay plantillas. Crea una en{" "}
                  <a href="/helpdesk/respuestas-rapidas" className="text-blue-600 hover:underline">Helpdesk → Respuestas rápidas</a>.
                </div>
              )}
            </div>
            <DialogFooter className="border-t pt-3 mt-2">
              <a
                href="/helpdesk/respuestas-rapidas"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline mr-auto"
              >
                Administrar respuestas rápidas →
              </a>
              <Button variant="outline" onClick={() => { setQrPickerOpen(false); setQrSearch(""); }}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Perfil de Viajero vinculado al ticket */}
        <PerfilViajeroDialog
          open={perfilOpen}
          onClose={() => setPerfilOpen(false)}
          data={perfilInfo}
        />
      </div>
    </div>
  );
};

// ══════════════ Perfil de Viajero (visor de solo lectura) ══════════════
// Muestra los datos del perfil ligado y permite ver/descargar los adjuntos
// (identificaciones, visas, etc.) desde Helpdesk sin dejar el ticket.
const PerfilViajeroDialog = ({ open, onClose, data }) => {
  const [preview, setPreview] = useState(null); // { url, filename, label }

  if (!data?.has_perfil) return null;
  const p = data.perfil || {};
  const datos = p.datos || {};

  // Documentos del perfil: fuente primaria es `p.documentos[]`
  // (INE, pasaporte, visa, etc.), con `id`/`campo`/`filename`.
  const docs = (p.documentos || []);

  // Descarga forzada del archivo (mantiene el flujo tradicional)
  const downloadDoc = async (docId, filename) => {
    try {
      const r = await axios.get(`${API}/viajeros-ext/perfiles/${p.id}/archivo/${docId}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descargar el documento");
    }
  };

  // Formatear etiqueta amigable del campo del catálogo
  const DOC_LABELS = {
    idArchivo: "Identificación",
    docVisa: "Visa",
    docVacuna: "Certificado de vacunación",
    docOtros: "Otros documentos",
  };

  const groups = [
    { title: "Datos personales", rows: [
      ["Nombre completo", [datos.nombres, datos.apellidoP, datos.apellidoM].filter(Boolean).join(" ")],
      ["Email", datos.email],
      ["Teléfono celular", datos.telCel],
      ["Teléfono alterno", datos.telAlt],
      ["Género", datos.genero],
      ["Nacionalidad", datos.nacionalidad],
      ["Fecha de nacimiento", datos.fechaNac],
    ]},
    { title: "Dirección", rows: [
      ["Dirección", datos.direccion],
      ["Ciudad", datos.ciudad],
      ["C.P.", datos.cp],
    ]},
    { title: "Contacto de emergencia", rows: [
      ["Nombre", [datos.emNombre, datos.emApellido].filter(Boolean).join(" ")],
      ["Parentesco", datos.emParentesco],
      ["Teléfono", datos.emTel],
      ["Email", datos.emEmail],
    ]},
    { title: "Notas", rows: [
      ["Alergias / condiciones médicas", datos.alergias],
      ["Comentarios", datos.comentarios],
    ]},
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="perfil-viajero-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRoundCog className="w-5 h-5 text-indigo-600" />
            Perfil de Viajero
            {p.folio && <span className="text-xs font-mono text-slate-500 ml-2">{p.folio}</span>}
            {data.project_nombre && <Badge variant="outline" className="ml-2">{data.project_nombre}</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {groups.map((g) => {
            const has = g.rows.some(([, v]) => v && String(v).trim());
            if (!has) return null;
            return (
              <div key={g.title}>
                <p className="text-xs font-semibold uppercase text-slate-500 mb-1">{g.title}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  {g.rows.map(([label, value]) => {
                    if (!value || !String(value).trim()) return null;
                    return (
                      <div key={label} className="flex gap-2">
                        <span className="text-slate-500 min-w-[9rem]">{label}:</span>
                        <span className="text-slate-800 flex-1">{value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {docs.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase text-slate-500 mb-1">
                Documentos ({docs.length})
              </p>
              <p className="text-[11px] text-slate-500 mb-2 italic">
                Solo lectura desde Helpdesk. Usa <b>Ver</b> para vista previa y <b>Descargar</b> para guardar el archivo.
              </p>
              <ul className="space-y-1.5">
                {docs.map((f, i) => {
                  const label = DOC_LABELS[f.campo || f.tipo] || (f.campo || f.tipo || `Archivo ${i + 1}`);
                  return (
                    <li key={f.id || i} className="flex items-center gap-2 text-sm border border-slate-200 rounded px-2 py-1.5 bg-slate-50">
                      <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800">{label}</div>
                        <div className="text-xs text-slate-500 truncate" title={f.filename}>
                          {f.filename || "—"}
                          {f.fecha_vencimiento && <span className="ml-2 text-amber-700">· vence {f.fecha_vencimiento}</span>}
                          {f.numero_documento && <span className="ml-2 text-slate-500">· {f.numero_documento}</span>}
                        </div>
                      </div>
                      {f.id && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            onClick={() => setPreview({
                              url: `${API}/viajeros-ext/perfiles/${p.id}/archivo/${f.id}?inline=1`,
                              filename: f.filename,
                              label,
                            })}
                            data-testid={`hd-perfil-doc-ver-${f.campo || f.tipo}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7"
                            onClick={() => downloadDoc(f.id, f.filename)}
                            data-testid={`hd-perfil-doc-descargar-${f.campo || f.tipo}`}
                            title="Descargar archivo"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
          </div>
        </div>

        <DocumentPreviewDialog
          open={!!preview}
          onClose={() => setPreview(null)}
          url={preview?.url}
          filename={preview?.filename}
          label={preview?.label}
          testIdPrefix="hd-perfil-doc-preview"
        />
      </DialogContent>
    </Dialog>
  );
};

export default HelpdeskTicketDetail;
